import { DateTime } from 'luxon';

export function isValidDateTime(value: unknown): value is DateTime {
  // Brand check so values from another Luxon copy still count.
  return DateTime.isDateTime(value) && value.isValid;
}

/** MUI pickers crash if they receive an invalid Luxon DateTime. */
export function toPickerDateTime(value: unknown): DateTime | null {
  return isValidDateTime(value) ? value : null;
}

/**
 * Re-labels a picker value with `zone` while keeping its wall-clock time.
 * Pickers emit values in the browser's zone, but the trip's times are meant
 * in the pickup address's zone (calculate-pickup-time already reads the
 * appointment's HH:mm that way), so compare them there instead of as instants.
 */
export function inZoneKeepingLocalTime(
  value: unknown,
  zone: string | null | undefined,
): DateTime | null {
  const dt = toPickerDateTime(value);
  if (!dt || !zone) return dt;
  const next = dt.setZone(zone, { keepLocalTime: true });
  return isValidDateTime(next) ? next : dt;
}

export function isFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function isFiniteOrZero(value: unknown): number {
  return isFiniteNumber(value) ?? 0;
}

export function hasFiniteTimeParts(value: DateTime): boolean {
  return (
    Number.isFinite(value.hour) &&
    Number.isFinite(value.minute) &&
    Number.isFinite(value.second)
  );
}

export function hasFiniteDateParts(value: DateTime): boolean {
  return (
    Number.isFinite(value.year) &&
    Number.isFinite(value.month) &&
    Number.isFinite(value.day)
  );
}

function durationUnitsAreFinite(duration: Record<string, number>): boolean {
  return Object.values(duration).every((value) => Number.isFinite(value));
}

export function safeMinus(
  value: DateTime,
  duration: Record<string, number>,
): DateTime | null {
  if (!isValidDateTime(value) || !durationUnitsAreFinite(duration)) {
    return null;
  }
  const next = value.minus(duration);
  return isValidDateTime(next) ? next : null;
}

export function safePlus(
  value: DateTime,
  duration: Record<string, number>,
): DateTime | null {
  if (!isValidDateTime(value) || !durationUnitsAreFinite(duration)) {
    return null;
  }
  const next = value.plus(duration);
  return isValidDateTime(next) ? next : null;
}

export function mergeDatePart(
  base: DateTime | null,
  newDate: DateTime | null,
): DateTime | null {
  if (newDate === null) {
    return null;
  }
  if (!isValidDateTime(newDate) || !hasFiniteDateParts(newDate)) {
    return toPickerDateTime(base);
  }
  if (!isValidDateTime(base)) {
    return null;
  }
  const next = base.set({
    year: newDate.year,
    month: newDate.month,
    day: newDate.day,
  });
  return isValidDateTime(next) ? next : base;
}

export function mergeTimePart(
  base: DateTime | null,
  newTime: DateTime | null,
): DateTime | null {
  if (newTime === null) {
    return null;
  }
  if (!isValidDateTime(newTime) || !hasFiniteTimeParts(newTime)) {
    return toPickerDateTime(base);
  }
  if (!isValidDateTime(base)) {
    return newTime;
  }
  const next = base.set({
    hour: newTime.hour,
    minute: newTime.minute,
    second: newTime.second,
  });
  return isValidDateTime(next) ? next : base;
}

/**
 * Shared calendar-date predicates for the pickup/return restriction: pickup
 * must be on the appointment's day or up to one day before it; return must
 * be on the appointment's day or up to one day after it. Compares the dates
 * as shown, with no zone conversion: the appointment is in the browser's
 * zone and pickup/return may be in the trip's, so converting would push e.g.
 * a 10 PM PT pickup to the next day for a CT user. See: F1V2-6687. Used by
 * the date pickers' field-level validation, the schema's submit-time
 * backstop, and the pickup auto-calculation, so a single definition can't
 * drift out of sync between them.
 */
export function isWithinPickupDateRange(
  candidate: DateTime,
  appointmentDateTime: DateTime,
): boolean {
  if (!isValidDateTime(candidate) || !isValidDateTime(appointmentDateTime)) {
    return false;
  }
  const candidateDate = candidate.toISODate();
  const apptDate = appointmentDateTime.toISODate();
  const minDate = safeMinus(appointmentDateTime, { days: 1 })?.toISODate();
  if (!candidateDate || !apptDate || !minDate) {
    return false;
  }
  return candidateDate >= minDate && candidateDate <= apptDate;
}

export function isWithinReturnDateRange(
  candidate: DateTime,
  appointmentDateTime: DateTime,
): boolean {
  if (!isValidDateTime(candidate) || !isValidDateTime(appointmentDateTime)) {
    return false;
  }
  const candidateDate = candidate.toISODate();
  const apptDate = appointmentDateTime.toISODate();
  const maxDate = safePlus(appointmentDateTime, { days: 1 })?.toISODate();
  if (!candidateDate || !apptDate || !maxDate) {
    return false;
  }
  return candidateDate >= apptDate && candidateDate <= maxDate;
}

/**
 * Pickup time from a calculate-pickup-time response. Returns null when the
 * estimate is too incomplete to compute a valid DateTime (missing/NaN offset,
 * unparseable ISO, or invalid timezone) so callers can skip instead of throw.
 * A pickUpDueDateTime from a stale estimate (fetched before the appointment
 * was last changed, and not yet re-fetched) is rejected in favor of the
 * totalPickupTime fallback, which is always computed from the current
 * appointmentDateTime and so is always in range.
 */
export function calculatePickupDateTime(
  appointmentDateTime: DateTime,
  estimatedPickupTime: {
    pickUpDueDateTime?: string | null;
    timeZone?: string | null;
    totalPickupTime?: number | null;
  },
): DateTime | null {
  if (!isValidDateTime(appointmentDateTime)) {
    return null;
  }

  if (estimatedPickupTime.pickUpDueDateTime) {
    const fromApi = DateTime.fromISO(
      estimatedPickupTime.pickUpDueDateTime,
      estimatedPickupTime.timeZone
        ? { zone: estimatedPickupTime.timeZone }
        : undefined,
    );
    if (
      isValidDateTime(fromApi) &&
      isWithinPickupDateRange(fromApi, appointmentDateTime)
    ) {
      return fromApi;
    }
  }

  const minutes = isFiniteNumber(estimatedPickupTime.totalPickupTime);
  if (minutes === null) {
    return null;
  }
  return safeMinus(appointmentDateTime, { minutes });
}

export function defaultTimePickerReferenceDate(): DateTime {
  return DateTime.now().startOf('day');
}

/**
 * True when a pickup is closer to the appointment than the required lead time.
 * A non-positive offset (including NaN coerced to 0) is not a violation.
 */
export function isPickupLeadTimeTooShort(
  pickup: unknown,
  appointment: unknown,
  offsetMinutes: number,
): boolean {
  if (!(offsetMinutes > 0)) return false;
  if (!isValidDateTime(pickup) || !isValidDateTime(appointment)) return false;
  const minutesUntilAppointment = appointment.diff(pickup, 'minutes').minutes;
  return minutesUntilAppointment < offsetMinutes;
}

type PickupOffsetTrip = {
  isWillCall?: boolean;
  pickupDateTime?: unknown;
  appointmentDateTime?: unknown;
  pickupTimeZone?: string | null;
};

/**
 * Client-portal pickup lead-time violation. Matches the message shown under
 * the pickup time field by comparing wall-clock values in each trip's
 * pickupTimeZone. Will-call legs and non-client portals are exempt.
 */
export function hasClientPortalPickupOffsetError(
  isClientPortal: boolean,
  trips: ReadonlyArray<PickupOffsetTrip> | null | undefined,
  offsetMinutesByTrip: ReadonlyArray<number>,
): boolean {
  if (!isClientPortal || !trips?.length) return false;
  return trips.some((trip, index) => {
    if (trip?.isWillCall) return false;
    const zone = trip?.pickupTimeZone;
    return isPickupLeadTimeTooShort(
      inZoneKeepingLocalTime(trip?.pickupDateTime, zone),
      inZoneKeepingLocalTime(trip?.appointmentDateTime, zone),
      offsetMinutesByTrip[index] ?? 0,
    );
  });
}
